import React, { useCallback, useEffect, useState } from 'react';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Copy, RefreshCw, XCircle, Link2, Landmark } from 'lucide-react';
import RazorpayLinkDialog from '@/components/RazorpayLinkDialog';
import RazorpayPayoutDialog from '@/components/RazorpayPayoutDialog';

const authHeaders = () => {
  const h = {};
  const t = localStorage.getItem('session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
};

const statusColor = (s) => ({
  paid: 'bg-emerald-100 text-emerald-800', processed: 'bg-emerald-100 text-emerald-800',
  created: 'bg-amber-100 text-amber-800', queued: 'bg-amber-100 text-amber-800',
  processing: 'bg-blue-100 text-blue-800', pending: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-gray-200 text-gray-600', expired: 'bg-gray-200 text-gray-600',
  failed: 'bg-red-100 text-red-700', reversed: 'bg-red-100 text-red-700', rejected: 'bg-red-100 text-red-700',
}[s] || 'bg-gray-100 text-gray-700');

export const RazorpayPanel = ({ tourId = '', tours = [], prefill = {}, compact = false }) => {
  const [links, setLinks] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showLink, setShowLink] = useState(false);
  const [showPayout, setShowPayout] = useState(false);

  useEffect(() => {
    fetch(`${API}/pollen/vendors`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((v) => setVendors(Array.isArray(v) ? v : []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const q = tourId ? `?tour_id=${tourId}` : '';
    try {
      const [lr, pr] = await Promise.all([
        fetch(`${API}/razorpay/payment-links${q}`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API}/razorpay/payouts${q}`, { credentials: 'include', headers: authHeaders() }),
      ]);
      if (lr.ok) setLinks(await lr.json());
      if (pr.ok) setPayouts(await pr.json());
    } catch (e) { console.error(e); }
  }, [tourId]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, okMsg) => {
    const res = await fetch(`${API}/razorpay/${path}`, { method: 'POST', credentials: 'include', headers: authHeaders() });
    if (res.ok) { okMsg && toast.success(okMsg); load(); }
    else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="razorpay-panel">
      <div className="flex flex-wrap gap-2">
        <Button size={compact ? 'sm' : 'default'} onClick={() => setShowLink(true)} data-testid="razorpay-new-link-btn">
          <Link2 className="w-4 h-4 mr-1" />Payment Link</Button>
        <Button size={compact ? 'sm' : 'default'} variant="outline" onClick={() => setShowPayout(true)} data-testid="razorpay-new-payout-btn">
          <Landmark className="w-4 h-4 mr-1" />Pay Vendor</Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          <p className="text-sm font-semibold mb-2 flex items-center gap-1"><Link2 className="w-3.5 h-3.5" />Payment Links ({links.length})</p>
          {links.length === 0 ? <p className="text-sm text-muted-foreground py-2">No payment links yet — create one to collect from a customer.</p> : (
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow>
                <TableHead>Customer</TableHead><TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {links.map((l) => (
                  <TableRow key={l.id} data-testid={`razorpay-link-row-${l.id}`}>
                    <TableCell className="font-medium">{l.customer_name || '-'}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[220px] truncate">{l.description}</TableCell>
                    <TableCell className="text-right font-medium">₹{l.amount?.toLocaleString('en-IN')}</TableCell>
                    <TableCell><Badge className={statusColor(l.status)}>{l.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Copy link"
                          onClick={() => { navigator.clipboard.writeText(l.short_url); toast.success('Link copied'); }}><Copy className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Refresh status"
                          onClick={() => act(`payment-links/${l.id}/refresh`)}><RefreshCw className="w-3.5 h-3.5" /></Button>
                        {['created', 'partially_paid'].includes(l.status) && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" title="Cancel link"
                            onClick={() => act(`payment-links/${l.id}/cancel`, 'Link cancelled')}><XCircle className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <p className="text-sm font-semibold mb-2 flex items-center gap-1"><Landmark className="w-3.5 h-3.5" />Vendor Payouts ({payouts.length})</p>
          {payouts.length === 0 ? <p className="text-sm text-muted-foreground py-2">No payouts yet.</p> : (
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow>
                <TableHead>Vendor</TableHead><TableHead>Mode</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>UTR</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {payouts.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.vendor_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.mode}</TableCell>
                    <TableCell className="text-right font-medium">₹{p.amount?.toLocaleString('en-IN')}</TableCell>
                    <TableCell><Badge className={statusColor(p.status)}>{p.status}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.utr || '-'}</TableCell>
                    <TableCell><Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Refresh status"
                      onClick={() => act(`payouts/${p.id}/refresh`)}><RefreshCw className="w-3.5 h-3.5" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          )}
        </CardContent>
      </Card>

      <RazorpayLinkDialog open={showLink} onClose={() => setShowLink(false)} tours={tours}
        prefill={{ tour_id: tourId || '', ...prefill }} onCreated={load} />
      <RazorpayPayoutDialog open={showPayout} onClose={() => setShowPayout(false)} vendors={vendors} tours={tours}
        prefill={{ tour_id: tourId || '' }} onCreated={load} />
    </div>
  );
};

export default RazorpayPanel;
